import { useState, useEffect } from 'react';
import { useMCPToolAgent } from '../useMCPToolAgent';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { VoiceOrchestrationEngine } from '@/utils/voice/VoiceOrchestrationEngine';

export const useElevenLabsScreeningAgent = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [voiceOrchestrator, setVoiceOrchestrator] = useState<VoiceOrchestrationEngine | null>(null);
  const elevenLabs = useMCPToolAgent('elevenlabs');

  useEffect(() => {
    checkConfiguration();
    setVoiceOrchestrator(new VoiceOrchestrationEngine());
  }, []);

  const checkConfiguration = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      const { data } = await supabase
        .from('platform_credentials')
        .select('is_active')
        .eq('platform', 'elevenlabs')
        .eq('user_id', user.id)
        .single();
      
      if (data && data.is_active) {
        setIsConfigured(true);
      }
    } catch (error) {
      console.error('Error checking ElevenLabs configuration:', error);
    }
  };

  const synthesizeSpeech = async (
    text: string, 
    voiceId?: string, 
    model?: string,
    stability?: number,
    similarityBoost?: number
  ) => {
    try {
      if (!isConfigured) {
        toast.error('ElevenLabs is not configured. Please add your API key in Integrations.');
        throw new Error('ElevenLabs not configured');
      }
      
      setIsProcessing(true);
      const result = await elevenLabs.callMethod('textToSpeech', {
        text,
        voiceId: voiceId || '21m00Tcm4TlvDq8ikWAM', // Default to 'Rachel'
        model: model || 'eleven_monolingual_v1',
        stability,
        similarityBoost
      });
      return result;
    } catch (error) {
      console.error('Error synthesizing speech:', error);
      toast.error('Failed to synthesize speech');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const getAvailableVoices = async () => {
    try {
      if (!isConfigured) {
        toast.error('ElevenLabs is not configured. Please add your API key in Integrations.');
        return [];
      }
      
      const result = await elevenLabs.callMethod('getVoices');
      return result.voices;
    } catch (error) {
      console.error('Error fetching voices:', error);
      toast.error('Failed to fetch available voices');
      return [];
    }
  };

  const getAvailableModels = async () => {
    try {
      if (!isConfigured) {
        toast.error('ElevenLabs is not configured. Please add your API key in Integrations.');
        return [];
      }
      
      const result = await elevenLabs.callMethod('getModels');
      return result.models;
    } catch (error) {
      console.error('Error fetching models:', error);
      toast.error('Failed to fetch available models');
      return [];
    }
  };

  const generateContextualSpeech = async (
    text: string,
    context: {
      scenario: 'technical_interview' | 'hr_interview' | 'initial_screening' | 'follow_up',
      phase: 'introduction' | 'technical_questions' | 'behavioral_questions' | 'closing',
      candidateData?: any,
      emphasis?: 'normal' | 'high' | 'low'
    }
  ) => {
    if (!voiceOrchestrator) {
      console.error("Voice orchestrator not initialized");
      return null;
    }

    try {
      const enhancedText = voiceOrchestrator.enhanceText(text, {
        isQuestion: context.phase.includes('questions'),
        emphasis: context.emphasis === 'high' ? ['important', 'key'] : [],
        technicalTerms: context.phase === 'technical_questions' ? 
          extractTechnicalTerms(text) : []
      });

      const result = await synthesizeSpeech(
        enhancedText,
        voiceOrchestrator.getVoiceForContext(context)
      );

      return result?.audioContent || null;
    } catch (error) {
      console.error("Failed to generate contextual speech:", error);
      toast.error("Failed to generate speech with context");
      return null;
    }
  };

  const extractTechnicalTerms = (text: string): string[] => {
    const commonTechTerms = [
      'API', 'REST', 'GraphQL', 'React', 'Node.js', 'TypeScript',
      'JavaScript', 'Python', 'Docker', 'Kubernetes', 'AWS',
      'CI/CD', 'DevOps', 'SQL', 'NoSQL', 'MongoDB'
    ];
    
    return commonTechTerms.filter(term => 
      text.toLowerCase().includes(term.toLowerCase())
    );
  };

  return {
    synthesizeSpeech,
    getAvailableVoices,
    getAvailableModels,
    isProcessing,
    isConfigured,
    generateContextualSpeech,
    voiceOrchestrator
  };
};
